import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Add,
  Assignment,
  RemoveCircle,
  AddCircle,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function TeacherExams() {
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableGrades, setAvailableGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'midterm',
    grade: '',
    exam_date: '',
    description: '',
  });

  const [examSubjects, setExamSubjects] = useState([
    { subject_name: '', total_marks: 100, passing_marks: 35 },
  ]);

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchExams();
  }, [selectedGrade]);

  useEffect(() => {
    const filtered = exams.filter(
      (exam) =>
        exam.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredExams(filtered);
  }, [searchTerm, exams]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch teacher's profile to get their subjects
      const profileResponse = await axios.get(`${API_URL}/teacher/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const teacherSubjects = profileResponse.data.teacher?.subjects || [];
      setAvailableSubjects(teacherSubjects.length > 0 ? teacherSubjects : ['Mathematics', 'Science']);
      
      // Fetch available grades
      const gradesResponse = await axios.get(`${API_URL}/students/filters/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableGrades(gradesResponse.data.grades || [6, 7, 8, 9, 10, 11]);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setAvailableGrades([6, 7, 8, 9, 10, 11]);
      setAvailableSubjects(['Mathematics', 'Science']);
    }
  };

  const fetchExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedGrade) {
        params.grade = selectedGrade;
      }
      
      const response = await axios.get(`${API_URL}/teacher/exams`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setExams(response.data);
      setFilteredExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setSelectedExam(exam);
      setFormData({
        name: exam.name,
        type: exam.type,
        grade: exam.grade,
        exam_date: exam.exam_date,
        description: exam.description || '',
      });
      
      const subjects = exam.examSubjects || exam.subjects || [];
      setExamSubjects(
        subjects.map((s) => ({
          subject_name: s.subject_name,
          total_marks: s.total_marks,
          passing_marks: s.passing_marks || 35,
        }))
      );
    } else {
      setSelectedExam(null);
      setFormData({
        name: '',
        type: 'midterm',
        grade: '',
        exam_date: '',
        description: '',
      });
      setExamSubjects([{ subject_name: '', total_marks: 100, passing_marks: 35 }]);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExam(null);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: null });
    }
  };

  const handleSubjectChange = (index, field, value) => {
    const newSubjects = [...examSubjects];
    newSubjects[index][field] = value;
    setExamSubjects(newSubjects);
  };

  const addSubject = () => {
    setExamSubjects([
      ...examSubjects,
      { subject_name: '', total_marks: 100, passing_marks: 35 },
    ]);
  };

  const removeSubject = (index) => {
    if (examSubjects.length > 1) {
      const newSubjects = examSubjects.filter((_, i) => i !== index);
      setExamSubjects(newSubjects);
    }
  };

  const handleSaveExam = async () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Exam name is required';
    if (!formData.grade) errors.grade = 'Grade is required';
    if (!formData.exam_date) errors.exam_date = 'Exam date is required';

    examSubjects.forEach((subject, index) => {
      if (!subject.subject_name) {
        errors[`subject_${index}`] = 'Subject name is required';
      }
      if (!subject.total_marks || subject.total_marks < 1) {
        errors[`marks_${index}`] = 'Total marks must be at least 1';
      }
    });

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        subjects: examSubjects,
      };

      if (selectedExam) {
        await axios.put(`${API_URL}/teacher/exams/${selectedExam.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Exam updated successfully!');
      } else {
        await axios.post(`${API_URL}/teacher/exams`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Exam created successfully!');
      }

      fetchExams();
      handleCloseDialog();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        alert('Failed to save exam. Please try again.');
      }
    }
  };

  const handleDeleteExam = async (examId, examName) => {
    if (!window.confirm(`Are you sure you want to delete "${examName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/teacher/exams/${examId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert('Exam deleted successfully!');
      fetchExams();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete exam.');
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      midterm: 'primary',
      final: 'error',
      quiz: 'warning',
      assignment: 'info',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      published: 'success',
      active: 'success',
      completed: 'info',
    };
    return colors[status] || 'default';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ fontSize: { xs: 32, md: 40 }, color: '#667eea', mr: 2 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: '#2d3748',
                fontSize: { xs: '1.5rem', md: '2.125rem' },
              }}>
                Manage Exams
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              onClick={() => handleOpenDialog()}
            >
              Create New Exam
            </Button>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search exams by name or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* Filter by Grade */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Grade</InputLabel>
                  <Select
                    value={selectedGrade}
                    label="Filter by Grade"
                    onChange={(e) => setSelectedGrade(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Grades</em>
                    </MenuItem>
                    {availableGrades.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {selectedGrade && (
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Active Filter:
                    </Typography>
                    <Chip
                      label={`Grade ${selectedGrade}`}
                      onDelete={() => setSelectedGrade('')}
                      sx={{
                        bgcolor: '#e0e7ff',
                        color: '#667eea',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', 
                textAlign: 'center',
                border: '2px solid #667eea',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#667eea' }}>
                  {exams.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Exams
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', 
                textAlign: 'center',
                border: '2px solid #4ade80',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#16a34a' }}>
                  {exams.filter(e => e.status === 'published' || e.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Published
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ 
                p: 2, 
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', 
                textAlign: 'center',
                border: '2px solid #f87171',
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#dc2626' }}>
                  {exams.filter(e => e.status === 'draft').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Drafts
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Exam Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Grade</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subjects</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                        No exams found. Click "Create New Exam" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExams.map((exam) => (
                    <TableRow key={exam.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {exam.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={exam.type.charAt(0).toUpperCase() + exam.type.slice(1)}
                          color={getTypeColor(exam.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Grade ${exam.grade}`}
                          size="small"
                          sx={{ bgcolor: '#e0e7ff', color: '#667eea', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(exam.exam_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(exam.examSubjects || exam.subjects || []).slice(0, 2).map((subject, idx) => (
                            <Chip
                              key={idx}
                              label={subject.subject_name}
                              size="small"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                          {(exam.examSubjects || exam.subjects || []).length > 2 && (
                            <Chip
                              label={`+${(exam.examSubjects || exam.subjects).length - 2}`}
                              size="small"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={exam.status.charAt(0).toUpperCase() + exam.status.slice(1)}
                          color={getStatusColor(exam.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          sx={{ color: '#667eea' }}
                          onClick={() => handleOpenDialog(exam)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: '#f87171' }}
                          onClick={() => handleDeleteExam(exam.id, exam.name)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ mr: 1, color: '#667eea' }} />
              {selectedExam ? 'Edit Exam' : 'Create New Exam'}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Exam Name *"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.type}>
                  <InputLabel>Exam Type *</InputLabel>
                  <Select
                    value={formData.type}
                    label="Exam Type *"
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <MenuItem value="midterm">Midterm</MenuItem>
                    <MenuItem value="final">Final</MenuItem>
                    <MenuItem value="quiz">Quiz</MenuItem>
                    <MenuItem value="assignment">Assignment</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.grade}>
                  <InputLabel>Grade *</InputLabel>
                  <Select
                    value={formData.grade}
                    label="Grade *"
                    onChange={(e) => handleInputChange('grade', e.target.value)}
                  >
                    {availableGrades.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Exam Date *"
                  type="date"
                  value={formData.exam_date}
                  onChange={(e) => handleInputChange('exam_date', e.target.value)}
                  error={!!formErrors.exam_date}
                  helperText={formErrors.exam_date}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </Grid>

              {/* Subjects Section */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Subjects *
                  </Typography>
                  <Button
                    startIcon={<AddCircle />}
                    onClick={addSubject}
                    size="small"
                    sx={{ color: '#667eea' }}
                  >
                    Add Subject
                  </Button>
                </Box>
                {examSubjects.map((subject, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f9fafb' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={!!formErrors[`subject_${index}`]}>
                          <InputLabel>Subject *</InputLabel>
                          <Select
                            value={subject.subject_name}
                            label="Subject *"
                            onChange={(e) => handleSubjectChange(index, 'subject_name', e.target.value)}
                          >
                            {availableSubjects.map((subj) => (
                              <MenuItem key={subj} value={subj}>
                                {subj}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors[`subject_${index}`] && (
                            <Typography variant="caption" color="error">
                              {formErrors[`subject_${index}`]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Total Marks *"
                          type="number"
                          value={subject.total_marks}
                          onChange={(e) => handleSubjectChange(index, 'total_marks', e.target.value)}
                          error={!!formErrors[`marks_${index}`]}
                          helperText={formErrors[`marks_${index}`]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Passing Marks"
                          type="number"
                          value={subject.passing_marks}
                          onChange={(e) => handleSubjectChange(index, 'passing_marks', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        {examSubjects.length > 1 && (
                          <IconButton
                            onClick={() => removeSubject(index)}
                            sx={{ color: '#f87171' }}
                          >
                            <RemoveCircle />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#64748b' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveExam}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                },
              }}
            >
              {selectedExam ? 'Update Exam' : 'Create Exam'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default TeacherExams;
